import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { DateTime } from "luxon";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { PackageCredit } from "app/utils/creditBundles";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { Loading } from "app/components/Wrappers";
import SessionsDatePicker from "../../DateTimeCoachSlots/_components/SessionsDatePicker";

import styles from "./ByDateBody.module.scss";
import useFacilityAvailability from "./useFacilityAvailability";
import CoachAvatar from "./CoachAvatar";
import { BookingFilterState, EMPTY_FILTERS } from "./BookingFilters";
import ConfirmBar from "../../SessionForm/ConfirmBar";
import OptionDescription from "./OptionDescription";
import {
  createPurchaseAndGetPath,
  PurchaseError,
} from "../../helpers/createPurchase";
import InfoModal from "app/routes/Calendar/InfoModal";
import {
  FacilitySlot,
  countOptions,
  groupSlotsByPartOfDay,
  nextAvailableDate,
  optionsForSlot,
} from "./byDateAvailability";

interface Props {
  coaches: CoachProfileModel[];
  /**
   * Coaches the header's search leaves visible. Availability is still requested
   * for the whole facility — the slot list and its coach counts describe the
   * facility, and refetching per keystroke would be N requests a character —
   * so this narrows only the options listed under the chosen slot.
   */
  visibleCoachIds?: string[];
  packageCredits?: Map<number, PackageCredit>;
  /** Where a chosen booking continues to — see FacilityBooking. */
  bookingRoute?: (coach: CoachProfileModel, rateId?: number) => string;
  /** See FacilityBooking — decides which scheduling surface to ask. */
  isPublicPage?: boolean;
  /** The filter row above: coach, session type and duration. */
  filters?: BookingFilterState;
}

/**
 * Options a coach shows before "See more". Four keeps every coach on two rows of
 * the two-column grid, so a coach with sixteen options does not bury the coaches
 * under them.
 */
const OPTIONS_BEFORE_SEE_MORE = 4;

// The plain words, not the shouted ones: the capitals are a `text-transform` on
// `.part_label`. See the note in SessionForm/DayRow.
const PART_LABELS: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
};

/**
 * "Book a Session / By Date" (frames 526:29419 and 526:29894): a month calendar
 * that only offers days with availability, one day's slots grouped into morning
 * / afternoon / evening with the number of coaches free at each, and — once a
 * slot is picked — every bookable option at that time, grouped by coach.
 *
 * Per comment #29 the screen shows a single day and the athlete navigates the
 * calendar; per #30 it opens on the next available day rather than today.
 */
const ByDateBody: React.FC<Props> = ({
  coaches,
  visibleCoachIds,
  packageCredits,
  bookingRoute = (coach, rateId) =>
    `/public/availability?coach=${coach.calendarUuid}` +
    (rateId ? `&sessionId=${rateId}` : ""),
  isPublicPage = true,
  filters = EMPTY_FILTERS,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [month, setMonth] = useState(DateTime.local().startOf("month"));
  const [date, setDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<FacilitySlot | null>(null);
  // #24 said picking a thing is the confirmation, and it is — of that step. The
  // booking itself still gets a Confirm, which is what the frame shows once an
  // option is chosen.
  const [chosen, setChosen] = useState<{
    coach: CoachProfileModel;
    rate: LessonRateModel;
  } | null>(null);
  // Coaches whose full list the athlete asked for.
  const [expandedCoaches, setExpandedCoaches] = useState<string[]>([]);
  const [purchaseError, setPurchaseError] = useState<PurchaseError | null>(
    null,
  );
  const optionsRef = useRef<HTMLDivElement | null>(null);

  /**
   * CFTB-3435: the same duration the options below are filtered by, asked of the
   * endpoints so the times offered are times of that length.
   */
  const filteredDuration =
    filters.duration === "all" ? null : Number(filters.duration) || null;

  /**
   * The coaches the calendar and the day's times are asked about.
   *
   * The coach filter used to narrow only the options listed under a chosen slot,
   * so picking a coach left the calendar's days and the times beside it as the
   * whole facility's — every time on the right kept saying "1 coach" while being
   * some other coach's. A filter that names one coach is a question about that
   * coach, so it is the question asked.
   *
   * The header's *search* still does not narrow it: that is a box being typed in,
   * and it would be a round of requests per keystroke — see `visibleCoachIds`.
   */
  const availabilityCoaches = useMemo(
    () =>
      filters.coachId === "all"
        ? coaches
        : coaches.filter((coach) => coach.id === filters.coachId),
    [coaches, filters.coachId],
  );

  /**
   * Asked per booking option, so each time lists only the options that offered
   * it. One rate per coach listed an option whose every bay was taken, and the
   * booking failed at Confirm. It also missed an open seat in a group option.
   */
  const { availableDates, datesLoading, slots, slotsLoading } =
    useFacilityAvailability(
      availabilityCoaches,
      month,
      date,
      isPublicPage,
      filteredDuration,
      true,
    );

  // #30: open on the next available day. Only until the athlete picks one —
  // after that their choice stands even when the month's availability reloads.
  useEffect(() => {
    if (date !== null || datesLoading || availableDates.size === 0) {
      return;
    }
    setDate(nextAvailableDate(availableDates, DateTime.local()));
  }, [availableDates, datesLoading, date]);

  /**
   * The day the athlete is on, once the filters change what is available.
   *
   * The calendar only offers days something can be booked on, so the only way to
   * be standing on a day with nothing on it is to have narrowed the screen after
   * choosing it — to one coach, or to a length nobody offers that day. Rather than
   * an empty afternoon, move to the next day that coach is free.
   */
  useEffect(() => {
    if (!date || datesLoading || availableDates.size === 0) {
      return;
    }
    if (!availableDates.has(date)) {
      setDate(nextAvailableDate(availableDates, DateTime.local()));
    }
  }, [availableDates, datesLoading, date]);

  // A slot belongs to the day it was loaded for.
  useEffect(() => {
    setSelectedSlot(null);
    setChosen(null);
  }, [date]);

  /**
   * A filter drops the chosen time only when the time itself is gone.
   *
   * Changing the coach or the length reloads the day, so a time chosen before it
   * may not survive — but usually it does, and dropping it either way threw the
   * athlete's choice away for a filter that only narrowed the options underneath
   * it. Which is what the suite caught: filter to one coach after picking 9:00 and
   * the options block disappeared instead of narrowing.
   *
   * So the reloaded day decides: the same start time still on offer keeps the
   * choice, and its absence clears it.
   */
  useEffect(() => {
    if (!selectedSlot || slotsLoading) {
      return;
    }
    const stillOffered = slots.find((slot) => slot.timeId === selectedSlot.timeId);
    if (!stillOffered) {
      setSelectedSlot(null);
      setChosen(null);
      return;
    }
    // The same time, but the coaches free at it may have changed with the filter.
    if (stillOffered !== selectedSlot) {
      setSelectedSlot(stillOffered);
    }
  }, [slots, slotsLoading, selectedSlot]);

  useEffect(() => {
    setChosen(null);
    setExpandedCoaches([]);
  }, [selectedSlot]);

  // The options appear below the slot grid, which on a busy day is well below the
  // fold — so picking a time takes the athlete to what they picked it for.
  useEffect(() => {
    if (!selectedSlot || !optionsRef.current) {
      return;
    }
    optionsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedSlot]);

  const slotGroups = useMemo(() => groupSlotsByPartOfDay(slots), [slots]);
  const optionGroups = useMemo(() => {
    let groups = optionsForSlot(coaches, selectedSlot, packageCredits);

    if (visibleCoachIds) {
      groups = groups.filter((group) => visibleCoachIds.includes(group.coach.id));
    }
    if (filters.coachId !== "all") {
      groups = groups.filter((group) => group.coach.id === filters.coachId);
    }

    // Duration narrows the options themselves, so a group with nothing left drops
    // out rather than showing an empty coach. Session Type is the Facility
    // Calendar's own filter — sessions or clinics — and this tab lists no clinics.
    if (filters.duration !== "all") {
      groups = groups
        .map((group) => ({
          ...group,
          options: group.options.filter(
            ({ rate }) =>
              String(rate.durationInMinutes) === filters.duration,
          ),
        }))
        .filter((group) => group.options.length > 0);
    }

    return groups;
  }, [coaches, selectedSlot, packageCredits, visibleCoachIds, filters]);

  const includeDates = useMemo(
    () => Array.from(availableDates).map((iso) => DateTime.fromISO(iso).toJSDate()),
    [availableDates],
  );

  const confirmBooking = async () => {
    if (!chosen || !selectedSlot) {
      return;
    }
    dispatch(
      setBookSession({
        coach: chosen.coach,
        pricingValue: chosen.rate,
        selectedSession: chosen.rate,
        date,
        time: {
          startHour: selectedSlot.startHour.toMillis(),
          endHour: selectedSlot.startHour
            .plus({ minutes: chosen.rate.durationInMinutes })
            .toMillis(),
          timeZone: selectedSlot.startHour.zoneName,
          coachId: chosen.coach.externalId,
        },
        duration: chosen.rate.durationInMinutes,
      }),
    );

    // Straight to the summary for the booking just made. The public flow has no
    // purchase to create until the visitor signs in, so it continues to the
    // calendar as before.
    if (isPublicPage) {
      // CFTB-3448: the session and the slot were both chosen here, so this asks for
      // the calendar rather than leaving the URL to speak for a visitor.
      history.push(bookingRoute(chosen.coach, chosen.rate.id), {
        sessionPicked: true,
      });
      return;
    }

    const path = await createPurchaseAndGetPath(setPurchaseError);
    if (path) {
      // Checkout sends "back" to a fixed booking screen; this tells it where the
      // athlete actually came from, tab and all.
      const returnUrl = `${location.pathname}${location.search}`;
      history.push(`${path}?return_url=${encodeURIComponent(returnUrl)}`);
    }
  };

  const optionDescription = (rate: LessonRateModel) =>
    rate.location
      ? t("{{duration}} session at {{location}}", {
          duration: rate.duration,
          location: rate.location,
        })
      : t("{{duration}} session", { duration: rate.duration });

  /**
   * CFTB-3436, again: navigating the month must not take the calendar with it.
   *
   * These two states used to replace the whole view. The month's days are loaded per
   * month, so stepping to one nobody is free in emptied the set — and with it the
   * calendar the athlete had just used to get there, which reads as the picker
   * closing itself. They belong beside the day's slots, where the calendar stays put
   * and the next month is one click away. The Facility Calendar tab was fixed this
   * way; the Dates tab is what QA found still standing.
   */
  const monthIsLoading = datesLoading && availableDates.size === 0;
  const monthHasNothing = !datesLoading && availableDates.size === 0;

  /**
   * CFTB-3474: no fold on this screen.
   *
   * A chevron in the card's corner used to put the calendar and the day's slots
   * away. It sat exactly on the calendar's own next-month arrow — measured at 390:
   * the fold at (311, 434) 28 square, the arrow at (313, 438) 23 square underneath
   * it — so tapping "next month" folded the card instead, and a phone had no way
   * forward through the months at all. QA asked for the arrow back and said the
   * collapse is not wanted here; it was my idea rather than the design's, so it
   * goes.
   */
  return (
    <>
      <div className={styles.panel}>
        <div className={styles.calendar_pane}>
          <SessionsDatePicker
            date={date ? DateTime.fromISO(date) : DateTime.local()}
            handleChangeDate={(value: DateTime) => setDate(value.toISODate())}
            handleDecreaseMonth={(decreaseMonth) => {
              setMonth(month.minus({ months: 1 }));
              decreaseMonth?.();
            }}
            handleIncreaseMonth={(increaseMonth) => {
              setMonth(month.plus({ months: 1 }));
              increaseMonth?.();
            }}
            includeDates={includeDates}
          />
        </div>

        <div className={styles.slots_pane}>
          <h2 className={styles.day_title}>
            {date
              ? DateTime.fromISO(date).toLocaleString(DateTime.DATE_FULL)
              : ""}
          </h2>

          {monthIsLoading || slotsLoading ? (
            <Loading isLoading loadType="spinner" />
          ) : monthHasNothing ? (
            <div className={styles.empty_state}>
              {t("No coaches at this facility have availability right now.")}
            </div>
          ) : slotGroups.length === 0 ? (
            <div className={styles.empty_state}>
              {t("No available timeslots on the selected day.")}
            </div>
          ) : (
            slotGroups.map((group) => (
              <div key={group.part} className={styles.slot_group}>
                <div className={styles.part_label}>
                  {t(PART_LABELS[group.part])}
                </div>
                <div className={styles.slot_grid}>
                  {group.slots.map((slot) => (
                    <button
                      key={slot.timeId}
                      type="button"
                      className={
                        selectedSlot?.timeId === slot.timeId
                          ? styles.slot_on
                          : styles.slot_off
                      }
                      onClick={() => setSelectedSlot(slot)}
                      data-testid="by-date-slot"
                    >
                      <span className={styles.slot_time}>
                        {slot.startHour.toFormat("h:mm a")}
                      </span>
                      <span className={styles.slot_meta}>
                        {t("{{count}} coaches", { count: slot.coachIds.length })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {selectedSlot && (
        <div className={styles.options_block} ref={optionsRef}>
          <div className={styles.options_count}>
            {t("{{count}} options", { count: countOptions(optionGroups) })}
          </div>

          {optionGroups.map((group) => {
            const expanded = expandedCoaches.includes(group.coach.id);
            const hidden = group.options.length - OPTIONS_BEFORE_SEE_MORE;
            const shown = expanded
              ? group.options
              : group.options.slice(0, OPTIONS_BEFORE_SEE_MORE);

            return (
            <div key={group.coach.id} className={styles.coach_block}>
              <div className={styles.coach_row}>
                <CoachAvatar
                  coach={group.coach}
                  className={styles.coach_avatar}
                  fontSize={16}
                />
                <div>
                  <div className={styles.coach_name}>{group.coach.fullName}</div>
                  <div className={styles.coach_option_count}>
                    {t("{{count}} options", { count: group.options.length })}
                  </div>
                </div>
              </div>

              <div className={styles.option_grid}>
                {shown.map(({ rate, packageCredit }) => (
                  <button
                    key={rate.id}
                    type="button"
                    className={
                      chosen?.rate.id === rate.id &&
                      chosen?.coach.id === group.coach.id
                        ? styles.option_card_selected
                        : styles.option_card
                    }
                    onClick={() => setChosen({ coach: group.coach, rate })}
                    data-testid="by-date-option"
                  >
                    <div className={styles.option_head}>
                      <div className={styles.option_duration}>
                        <span className={styles.option_duration_value}>
                          {rate.durationInMinutes}
                        </span>
                        <span className={styles.option_duration_unit}>
                          {t("MIN")}
                        </span>
                      </div>
                      <div className={styles.option_details}>
                        <div className={styles.option_title}>{rate.title}</div>
                        <div className={styles.option_description}>
                          {optionDescription(rate)}
                        </div>
                        {/*
                          CFTB-3457: the coach's own description of the option, under
                          the length-and-place line this screen derives. Read More
                          when it runs past two lines.
                        */}
                        <OptionDescription text={rate.description} />
                      </div>
                    </div>

                    {/* CN-2711: an option can hide its price. With nothing left to
                        state under the rule, the rule goes too — a divider with an
                        empty half below it reads as something failing to load. */}
                    {packageCredit ? (
                      <>
                        <hr className={styles.option_divider} />
                        <div className={styles.option_package}>
                          <i className="ico ico-star" />
                          {packageCredit.unlimited
                            ? t("Package: unlimited sessions")
                            : t("Package: {{count}} sessions remaining", {
                                count: packageCredit.remainingSessions,
                              })}
                        </div>
                      </>
                    ) : rate.hidePrice ? null : (
                      <>
                        <hr className={styles.option_divider} />
                        <div className={styles.option_price}>
                          {group.coach.currencySymbol}
                          {rate.rate}
                        </div>
                      </>
                    )}
                  </button>
                ))}
              </div>

              {hidden > 0 && (
                <button
                  type="button"
                  className={styles.see_more}
                  onClick={() =>
                    setExpandedCoaches((open) =>
                      expanded
                        ? open.filter((id) => id !== group.coach.id)
                        : [...open, group.coach.id],
                    )
                  }
                  data-testid="by-date-see-more"
                >
                  {expanded
                    ? t("Show less")
                    : t("See more ({{count}})", { count: hidden })}
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}

      <ConfirmBar
        time={
          chosen && selectedSlot
            ? {
                startHour: selectedSlot.startHour.toMillis(),
                endHour: selectedSlot.startHour
                  .plus({ minutes: chosen.rate.durationInMinutes })
                  .toMillis(),
                timeZone: selectedSlot.startHour.zoneName,
              }
            : null
        }
        optionTitle={chosen?.rate.title}
        onConfirm={confirmBooking}
        // CFTB-3426: clears the chosen option, which is what raised the bar, and
        // leaves the slot selected — the option cards it was covering are then
        // reachable and another can be picked without starting the day again.
        onDismiss={() => setChosen(null)}
      />
      <InfoModal
        isOpen={purchaseError !== null}
        title={purchaseError?.message || t("Unable to create booking")}
        status="error"
        onDismiss={() => setPurchaseError(null)}
      >
        <div>{purchaseError?.detail || t("Please try again.")}</div>
      </InfoModal>
    </>
  );
};

export default ByDateBody;
